import React, { Component, PropTypes } from 'react';
import omit from 'lodash/omit';
import partial from 'lodash/partial';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import classnames from 'classnames';
import {
  ResizableTextAreaField, TextAreaField, FileSelectorField, DEFAULT_REQUIRED_MESSAGE
} from 'common/components/form_fields';
import AnimatedButton from 'common/components/animated_button';
import DropdownMenu from 'common/components/dropdown_menu';
import HorizontalSelector from 'common/components/horizontal_selector';

/**
 * Message editor.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

class Form extends Component {
  renderPrepopulatedOpts() {
    const { prepopulatedOpts, change } = this.props;

    const buttons = (prepopulatedOpts || []).map(opt => ({
      show: true,
      label: opt.label,
      action: partial(change, 'body', opt.content)
    }));

    if (buttons.length === 0) {
      return null;
    }

    return (
      <DropdownMenu buttons={buttons} className="pull-right" />
    );
  }

  getSelectorOptions() {
    const { canPostInternal, wrapup } = this.props;

    if (!canPostInternal) {
      return [];
    }

    return [{
      label: wrapup? 'Wrap-up message' : 'Public message',
      value: false
    }, {
      label: 'Internal note',
      value: true,
    }];
  }

  getPlaceholder() {
    const {
      canPostInternal, internal, isClient, isCollaborator, hasCollaborators, wrapup
    } = this.props;

    if (!canPostInternal) {
      return '';
    }

    if (internal) {
      if (isClient) {
        return `Leave an internal note for yourself${hasCollaborators? ' or collaborators' : ''}...`;
      }

      if (isCollaborator) {
        return 'Leave an internal note for yourself, the client or other collaborators...';
      }

      return 'Leave an internal note...';
    }

    if (wrapup) {
      return 'Leave a private message for the winning creative...';
    }

    return 'Leave a public message for all participants...';
  }

  render() {
    const {
      error, handleSubmit, submitting, submitFailed, onCancel, label, actionLabel,
      attaching, onToggleAttach, wrapup, canAttach, isAdmin, internal, onChangeInternal
    } = this.props;
    const selectorOpts = this.getSelectorOptions();

    return (
      <form onSubmit={handleSubmit} className={internal? "internal" : ""}>
        <fieldset>
          { selectorOpts.length > 0 &&
          <HorizontalSelector options={selectorOpts} value={internal} onSelect={onChangeInternal} /> }

          <Field name="body" description={selectorOpts.length === 0? label : ''} rows={4}
            component={ResizableTextAreaField} placeholder={this.getPlaceholder()}/>

          { isAdmin && !wrapup &&
          <Field name="project_admin_note" description="Project Admin Note" component={TextAreaField} /> }

          {attaching && <Field
            name="attachment"
            label="Attach file"
            component={FileSelectorField}
            acceptedFileType=""
          />}

          <div className="form-button-block">
            <AnimatedButton
              processing={submitting}
              type="submit"
              label={actionLabel || 'Send'}
              processingLabel="Sending..."
              errorMsg={error || submitFailed}
              successMsg="Done!"
              baseBtnClass="btn-tertiary btn-block btn-md"
            />

            {canAttach && <button
              className={`btn btn-icon-only${attaching ? ' active' : ''}`}
              onClick={onToggleAttach}
            >
              <i className="fa fa-paperclip" />
            </button>}

            {onCancel &&
            <button className="btn btn-md btn-text" onClick={onCancel}>
              Cancel
            </button>}

            { this.renderPrepopulatedOpts() }
          </div>
        </fieldset>
      </form>
    );
  }
};

const validate = (values) => {
  const errors = {};
  const requiredFields = ['body'];
  for (let f of requiredFields) {
    if (!values[f]) errors[f] = DEFAULT_REQUIRED_MESSAGE;
  }
  return errors;
};

Form = reduxForm({
  form: 'MessageEditor',
  validate,
})(Form);

export default class MessageEditor extends Component {
  constructor(props) {
    super(props);

    this.state = { done: false, attaching: false, internal: false };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleAttach = this.toggleAttach.bind(this);
    this.onDone = this.onDone.bind(this);
  }

  onDone() {
    const { fetchMessagesIfNeeded, streamId, wrapup, wrapupStep, project, onDone } = this.props;

    /* fetch updated list of messages */
    fetchMessagesIfNeeded({ streamId, force: true, project, wrapup, wrapupStep });

    if (onDone) {
      onDone();
    }
  }

  handleSubmit(formData) {
    const { internal } = this.state;
    const {
      streamId, createMessageRequest, replyTo, wrapup, project, canPostInternal
    } = this.props;

    const data = {
      ...omit(formData, ['attachment']),
      ...formData.attachment,
      reply_to: replyTo,
      internal: canPostInternal? internal : false
    };

    return createMessageRequest(streamId, data, wrapup, project)
      .then(() => this.setState({ done: true }))
      .catch((res) => {
        /* field-specific errors */
        if (res.data) {
          for (const field of Object.keys(res.data)) {
            if (res.data[field]) throw new SubmissionError({ [field]: res.data[field][0] });
          }
        }
        /* global error */
        throw new SubmissionError({ _error: 'Unknown error. Please try again.' });
      });
  }

  toggleAttach(e) {
    e.preventDefault();
    this.setState({ attaching: !this.state.attaching });
  }

  render() {
    return (
      <div className="message-editor">
        {this.state.done ? <div className="done">
          <h3>
            <i className="fa fa-2x fa-check-circle" />
            <br /> Message Posted
          </h3>

          <div className="form-button-block">
            <button className="btn btn-tertiary btn-md" onClick={this.onDone}>Done</button>
          </div>
        </div> :
        <Form
          {...this.props}
          {...this.state}
          onToggleAttach={this.toggleAttach}
          onChangeInternal={v => this.setState({ internal: v })}
          onSubmit={this.handleSubmit}
        />}
      </div>
    );
  }
}


const EditorPropTypes = {
  streamId: PropTypes.number,
  replyTo: PropTypes.number,
  createMessageRequest: PropTypes.func,
  fetchMessagesIfNeeded: PropTypes.func,
  onDone: PropTypes.func,
  onCancel: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  actionLabel: PropTypes.string,
  canAttach: PropTypes.bool,
  canPostInternal: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isClient: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  hasCollaborators: PropTypes.bool,
  wrapup: PropTypes.object,
  wrapupStep: PropTypes.string,
  project: PropTypes.object,
  prepopulatedOpts: PropTypes.array,
};

Form.propTypes = EditorPropTypes;
MessageEditor.propTypes = EditorPropTypes;



// WEBPACK FOOTER //
// ./common/components/message_editor.jsx